"use client";
import { useEffect, useState } from "react";
import { generatePaymentCallbackUrl } from "@/lib/utils";
import Portal from "../../globalPortal";
import {
  IBaseModalProps,
  IIncreaseWalletDepositPayload,
  TCreditIncrementSchemaCombinePackage,
} from "@/interfaces";
import { useLockBodyScroll } from "@uidotdev/usehooks";
import { useGetWalletBalance, useIncreaseWalletDepositMutation } from "@/hooks";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useAppStore, useSecureStore } from "@/store";
import { useToastStore } from "@/store/toastStore";
import SufficientBalanceModal from "@/components/Payment/sufficientBalanceModal";
import InsufficientBalanceModal from "@/components/Payment/insufficientBalanceModal";
import { useSettings } from "@/hooks/useSettings";
import styles from "@/components/Payment/paymentCheckout.module.css";
import Logo from "@/components/logo";

interface Props extends IBaseModalProps {
  paymentSubmission: () => void;
  handleBackBtn?: () => void;
  submissionError: Error | null;
  submissionLoading: boolean;
  showManualError?: string | null;
  persistedData?: Record<string, any>;
  onErrorReset?: () => void;
  payBtnId?: string;
  pageData?: string;
  serviceId?: string | number;
  serviceCost?: string | number | null;
  isTimerActive?: boolean;
  timerEndTime?: number | null;
  onTimerComplete?: () => void;
}

export default function VehiclesPaymentModal({
  isOpen,
  onClose,
  paymentSubmission,
  submissionError,
  showManualError = null,
  submissionLoading = false,
  persistedData,
  onErrorReset,
  payBtnId,
  serviceCost,
  isTimerActive,
  onTimerComplete,
  timerEndTime,
}: Props) {
  const pathname = usePathname();
  const { data: session } = useSession();
  const {
    data: walletBalanceData,
    isLoading: isWalletBalanceLoading,
    error: walletBalanceError,
  } = useGetWalletBalance(session?.user.token);
  const { settings: allSettings } = useSettings(session?.user.token);
  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  const { setPersistedFields } = useSecureStore();
  const { setOpenToast } = useToastStore();
  const {
    isPending: isIncreaseSubmitting,
    mutate: increaseWalletDepositFn,
    error: increaseSubmittingError,
    reset: resetIncreaseWalletDeposit,
  } = useIncreaseWalletDepositMutation(session?.user.token);

  const walletBalance = Number(walletBalanceData?.data?.balance || 0);
  const resolvedServiceCost =
    serviceCost !== null &&
    serviceCost !== undefined &&
    Number(serviceCost) >= 0
      ? Number(serviceCost)
      : Number(allSettings?.price_download_pdf || 0);

  const isWalletBalanceInsufficient =
    walletBalance === 0 || walletBalance < resolvedServiceCost;

  useLockBodyScroll();

  const [isUseWallet, setIsUseWallet] = useState(false);

  // Reset errors when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetIncreaseWalletDeposit();
      if (onErrorReset) {
        onErrorReset();
      }
    }
  }, [isOpen, resetIncreaseWalletDeposit, onErrorReset]);

  // Reset errors when component unmounts
  useEffect(() => {
    return () => {
      resetIncreaseWalletDeposit();
      if (onErrorReset) {
        onErrorReset();
      }
    };
  }, [resetIncreaseWalletDeposit, onErrorReset]);

  // NOW THE EARLY RETURN - AFTER ALL HOOKS
  if (!isOpen || isWalletBalanceLoading) return null;

  // Handler functions
  const handleCreditIncrementSubmission = (
    data: TCreditIncrementSchemaCombinePackage & { is_topup?: boolean },
    bank?: string,
  ) => {
    const payload: IIncreaseWalletDepositPayload = {
      amount: data.amount,
      callback_url: data.callback,
      use_wallet: data.use_wallet,
      items: data.items,
      is_topup: data.is_topup,
    };

    increaseWalletDepositFn(
      { payload, bank },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathName);
            if (persistedData) {
              setPersistedFields(persistedData);
            }
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };

  const handlePayment = (data?: any) => {
    if (data?.topup) {
      handleCreditIncrementSubmission(
        {
          amount: Number(data.digit),
          callback: generatePaymentCallbackUrl(`${pathname}?s=nj`),
          use_wallet: false,
          is_topup: true,
        },
        data.bank,
      );
      return;
    }

    // Check if user selected wallet (p0) or has an existing package
    if (!isUseWallet && walletBalanceData?.data?.payment_type !== "postpaid") {
      const paymentData = {
        amount: isWalletBalanceInsufficient
          ? Number(data?.digit)
          : resolvedServiceCost,
        callback: generatePaymentCallbackUrl(`${pathname}?s=nj`),
        use_wallet: false,
      };
      const bank = data?.bank;
      handleCreditIncrementSubmission(paymentData, bank);
    } else {
      paymentSubmission();
    }
    useSecureStore.persist.clearStorage();
  };
  return (
    <Portal>
      <div className={styles.overlay} onMouseDown={onClose}>
        <section
          className={`${styles.checkout} ${styles.singleCheckout}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="vehicle-payment-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className={styles.header}>
            <div className={styles.headerTitle}>
              <span className={styles.brandMark}>
                <Logo width={35} height={35} />
              </span>
              <div>
                <h2 id="vehicle-payment-title">تأیید و پرداخت</h2>
                <p>دریافت فایل</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <span className={styles.secure}>پرداخت امن</span>
              <button
                className={styles.close}
                type="button"
                aria-label="بستن پنجره پرداخت"
                onClick={onClose}
              >
                <span className={styles.closeGlyph} aria-hidden="true" />
              </button>
            </div>
          </header>
          <div className={styles.body}>
            <section
              className={`${styles.summaryPanel} ${styles.singleSummary}`}
            >
              {isWalletBalanceInsufficient &&
              walletBalanceData?.data?.payment_type !== "postpaid" ? (
                <InsufficientBalanceModal
                  incrementBtnId={payBtnId}
                  onClose={onClose}
                  onSubmit={handlePayment}
                  walletBalance={walletBalanceData?.data?.balance}
                  inquiryPrice={resolvedServiceCost}
                  error={increaseSubmittingError}
                  isLoading={isIncreaseSubmitting}
                  selectedPackage={{
                    id: "p0",
                    name: "دریافت نتیجه استعلام",
                    price: String(resolvedServiceCost),
                  }}
                  showManualError={showManualError}
                />
              ) : (
                <SufficientBalanceModal
                  payBtnId={payBtnId}
                  onClose={onClose}
                  onSubmit={handlePayment}
                  paymentType={walletBalanceData?.data?.payment_type}
                  error={submissionError}
                  serviceType="دانلود فایل"
                  walletBalance={walletBalanceData?.data?.balance}
                  inquiryPrice={String(resolvedServiceCost)}
                  isLoading={submissionLoading}
                  showManualError={showManualError}
                  useWallet={isUseWallet}
                  walletHandler={setIsUseWallet}
                  selectedPackage={{
                    id: "p0",
                    name: "دریافت نتیجه استعلام",
                    price: String(resolvedServiceCost),
                  }}
                  isTimerActive={isTimerActive}
                  timerEndTime={timerEndTime}
                  onTimerComplete={onTimerComplete}
                />
              )}
            </section>
          </div>
        </section>
      </div>
    </Portal>
  );
}
